// Admin — Podcast scripts and audio generation
//
// Shows every podcast episode persisted by the Monthly Theme pipeline
// (`podcast_episodes` collection, keyed by (source_bundle_id, language)).
// For each episode the admin can:
//   • Expand the script to read it
//   • Click "Générer audio" → ElevenLabs TTS → stores MP3 inline
//   • Click "Préparer posts sociaux" → DeepSeek drafts captions
//   • Click "Publier via Social" → dispatches to X / Facebook / LinkedIn
//     through the existing /admin/social/dispatch route (ContentStudio)
import { useCallback, useEffect, useState } from "react";
import { Loader2, Mic, Share2, Wand2, PlayCircle } from "lucide-react";
import { apiFetch } from "@/lib/api";

const LANGS = ["fr", "en", "es", "pt"];

const AdminPodcastPage = () => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [flash, setFlash] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch("/api/admin/podcast/episodes");
      setEpisodes(r.ok ? (r.data?.items || []) : []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // Group episodes by source_bundle_id so each bundle appears once with 4 lang tabs.
  const bundles = Object.values(episodes.reduce((acc, ep) => {
    const k = ep.source_bundle_id || ep.slug;
    if (!acc[k]) {
      acc[k] = {
        bundle_id: k,
        theme: ep.theme,
        appellation_slug: ep.appellation_slug,
        updated_at: ep.updated_at,
        langs: {},
      };
    }
    acc[k].langs[ep.language || "fr"] = ep;
    if (ep.updated_at > acc[k].updated_at) acc[k].updated_at = ep.updated_at;
    return acc;
  }, {})).sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));

  const setBusyFor = (id, val) => setBusy((b) => ({ ...b, [id]: val }));

  const genAudio = async (bundleId, lang) => {
    setBusyFor(`${bundleId}:${lang}`, "audio"); setFlash("");
    try {
      const r = await apiFetch("/api/admin/podcast/generate-audio", {
        method: "POST", body: { source_bundle_id: bundleId, language: lang },
      });
      if (!r.ok) throw new Error(r.data?.detail || r.data?.error || "Erreur");
      setFlash(`Audio ${lang.toUpperCase()} généré pour ${bundleId} (${r.data?.audio_size_kb} Ko)`);
      await reload();
    } catch (e) { setFlash(`Erreur: ${e.message}`); }
    finally { setBusyFor(`${bundleId}:${lang}`, null); }
  };

  const prepSocial = async (bundleId) => {
    setBusyFor(`${bundleId}:social`, "prep"); setFlash("");
    try {
      const r = await apiFetch("/api/admin/podcast/prepare-social", {
        method: "POST", body: { source_bundle_id: bundleId },
      });
      if (!r.ok) throw new Error(r.data?.detail || "Erreur");
      setFlash(`Posts sociaux préparés pour ${bundleId}`);
      await reload();
    } catch (e) { setFlash(`Erreur: ${e.message}`); }
    finally { setBusyFor(`${bundleId}:social`, null); }
  };

  const pushSocial = async (bundleId) => {
    if (!window.confirm("Publier via Social (X / Facebook / LinkedIn via ContentStudio) ?")) return;
    setBusyFor(`${bundleId}:social`, "push"); setFlash("");
    try {
      const r = await apiFetch("/api/admin/podcast/dispatch-social", {
        method: "POST", body: { source_bundle_id: bundleId },
      });
      if (!r.ok) throw new Error(r.data?.detail || "Erreur");
      setFlash(`Social dispatch OK — ${bundleId}`);
      await reload();
    } catch (e) { setFlash(`Erreur: ${e.message}`); }
    finally { setBusyFor(`${bundleId}:social`, null); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-10" data-testid="admin-podcast">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Admin · Inbound</div>
        <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">Podcast</h1>
        <p className="text-[13.5px] text-[color:var(--gv-ink)]/65 mt-1 max-w-2xl">
          Scripts générés par le Thème du mois. Génère la version audio via ElevenLabs puis prépare et publie les posts sociaux via Social auto-publish.
        </p>
      </div>
      {flash && <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[12.5px] px-3 py-2 rounded-[5px]">{flash}</div>}

      {loading ? (
        <div className="p-10 text-center"><Loader2 className="animate-spin inline text-[color:var(--gv-pink)]"/></div>
      ) : bundles.length === 0 ? (
        <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-10 text-center text-[13px] text-[color:var(--gv-ink)]/55">
          Aucun script podcast généré. Lance d'abord un Thème du mois avec "Script podcast 4 langues" activé.
        </div>
      ) : (
        <div className="space-y-5">
          {bundles.map((b) => (
            <BundleCard key={b.bundle_id} bundle={b} busy={busy}
              onGenAudio={genAudio} onPrepSocial={prepSocial} onPushSocial={pushSocial}/>
          ))}
        </div>
      )}
    </div>
  );
};

function BundleCard({ bundle, busy, onGenAudio, onPrepSocial, onPushSocial }) {
  const [activeLang, setActiveLang] = useState("fr");
  const ep = bundle.langs[activeLang] || {};
  const social = bundle.langs.fr?.social_posts;
  return (
    <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] overflow-hidden" data-testid={`bundle-${bundle.bundle_id}`}>
      <div className="px-5 py-3 border-b border-[color:var(--gv-line)] flex items-center justify-between flex-wrap gap-2 bg-[color:var(--gv-mist)]/40">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-0.5">Thème du mois · {bundle.bundle_id.slice(0, 8)}</div>
          <h3 className="font-display text-lg text-[color:var(--gv-ink)]">{bundle.theme}</h3>
        </div>
        <div className="flex gap-2">
          <button onClick={() => onPrepSocial(bundle.bundle_id)}
            disabled={busy[`${bundle.bundle_id}:social`] === "prep"}
            className="text-[12.5px] px-3 py-1.5 border border-[color:var(--gv-line)] rounded-[4px] hover:bg-white inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid={`prep-social-${bundle.bundle_id}`}>
            {busy[`${bundle.bundle_id}:social`] === "prep" ? <Loader2 size={12} className="animate-spin"/> : <Wand2 size={12}/>}
            {social ? "Régénérer posts" : "Préparer posts"}
          </button>
          <button onClick={() => onPushSocial(bundle.bundle_id)}
            disabled={!social || busy[`${bundle.bundle_id}:social`] === "push"}
            className="text-[12.5px] px-3 py-1.5 bg-indigo-600 text-white rounded-[4px] hover:opacity-90 inline-flex items-center gap-1.5 disabled:opacity-40"
            data-testid={`push-social-${bundle.bundle_id}`}>
            {busy[`${bundle.bundle_id}:social`] === "push" ? <Loader2 size={12} className="animate-spin"/> : <Share2 size={12}/>}
            Publier via Social
          </button>
        </div>
      </div>
      <div className="px-5 pt-3 flex gap-1 border-b border-[color:var(--gv-line)]">
        {LANGS.map((lg) => {
          const has = !!bundle.langs[lg];
          const ready = bundle.langs[lg]?.audio_status === "audio_ready";
          return (
            <button key={lg} onClick={() => setActiveLang(lg)}
              className={`px-3 py-1.5 text-[12px] uppercase tracking-widest rounded-t-[4px] border border-b-0 ${
                activeLang === lg
                  ? "bg-white border-[color:var(--gv-line)] text-[color:var(--gv-pink)] font-semibold"
                  : "border-transparent text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-ink)]"
              }`}
              data-testid={`podcast-lang-${bundle.bundle_id}-${lg}`}>
              {lg}
              {!has && <span className="ml-1 text-[9px] text-amber-600">·vide</span>}
              {ready && <span className="ml-1 text-[9px] text-emerald-600">·🎧</span>}
            </button>
          );
        })}
      </div>
      <div className="p-5">
        {ep.title && (
          <div className="mb-3">
            <div className="font-display text-lg text-[color:var(--gv-ink)]">{ep.title}</div>
            <div className="text-[11px] text-[color:var(--gv-ink)]/55 uppercase tracking-widest mt-0.5">
              {Math.round((ep.duration_sec || 180) / 60)} min · voix {ep.audio_voice_id ? ep.audio_voice_id : "default"}
            </div>
          </div>
        )}
        {ep.intro && (
          <div className="text-[13.5px] italic text-[color:var(--gv-ink)]/75 mb-3">{ep.intro}</div>
        )}
        {ep.audio_url && ep.audio_status === "audio_ready" && (
          <div className="mb-3 bg-emerald-50 border border-emerald-200 rounded-[5px] p-3">
            <div className="flex items-center gap-2 text-[12px] text-emerald-700 mb-2">
              <PlayCircle size={14}/> MP3 prêt · {ep.audio_size_kb} Ko · généré {new Date(ep.audio_generated_at).toLocaleString()}
            </div>
            <audio controls src={ep.audio_url} className="w-full" data-testid={`audio-${bundle.bundle_id}-${activeLang}`}/>
          </div>
        )}
        <details className="mb-3">
          <summary className="cursor-pointer text-[12.5px] text-[color:var(--gv-pink)]">Script complet ({ep.body_markdown?.length || 0} car.)</summary>
          <pre className="text-[12.5px] bg-[color:var(--gv-mist)] p-3 rounded-[5px] whitespace-pre-wrap mt-2 max-h-96 overflow-y-auto">{ep.body_markdown || "(vide)"}</pre>
        </details>
        {ep.outro && <div className="text-[13px] text-[color:var(--gv-ink)]/80">{ep.outro}</div>}

        <div className="mt-4 flex gap-2">
          <button onClick={() => onGenAudio(bundle.bundle_id, activeLang)}
            disabled={!ep.body_markdown || busy[`${bundle.bundle_id}:${activeLang}`] === "audio"}
            className="text-[12.5px] px-3 py-1.5 bg-[color:var(--gv-pink)] text-white rounded-[4px] hover:opacity-90 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid={`gen-audio-${bundle.bundle_id}-${activeLang}`}>
            {busy[`${bundle.bundle_id}:${activeLang}`] === "audio" ? <Loader2 size={12} className="animate-spin"/> : <Mic size={12}/>}
            {ep.audio_status === "audio_ready" ? "Régénérer audio" : "Générer audio"} ({activeLang.toUpperCase()})
          </button>
        </div>

        {social && (
          <div className="mt-5 pt-4 border-t border-[color:var(--gv-line)]">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60 mb-2">Posts sociaux préparés</div>
            {social.fr?.excerpt_fr && <div className="mb-2 bg-[color:var(--gv-mist)]/50 p-2.5 rounded-[4px] text-[12.5px]"><strong>FB/accroche FR :</strong> {social.fr.excerpt_fr}</div>}
            {social.fr?.x_caption && <div className="mb-2 bg-[color:var(--gv-mist)]/50 p-2.5 rounded-[4px] text-[12.5px]"><strong>X :</strong> {social.fr.x_caption}</div>}
            {social.fr?.linkedin_caption && <div className="mb-2 bg-[color:var(--gv-mist)]/50 p-2.5 rounded-[4px] text-[12.5px]"><strong>LinkedIn :</strong> {social.fr.linkedin_caption}</div>}
            {social.en?.excerpt_en && <div className="mb-2 bg-[color:var(--gv-mist)]/50 p-2.5 rounded-[4px] text-[12.5px]"><strong>EN :</strong> {social.en.excerpt_en}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPodcastPage;
